import './public-path';
import { onAuthStateChanged } from 'firebase/auth';

import Vue from 'vue';
import Vuelidate from 'vuelidate';
import VueMeta from 'vue-meta';
import VueExpandableImage from 'vue-expandable-image';
// TODO: use TAvatar instead of the package
import VueLetterAvatar from 'vue-letter-avatar';
import ReactiveProvide from 'vue-reactive-provide';
// for vue 3 we can use https://floating-ui.com/docs/vue
import App from '@/App.vue';
import router from '@/router';
import store from '@/store';

import { auth } from '@/services/firebase';

import '@/assets/scss/index.scss';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'cropperjs/dist/cropper.css';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import { formatNumber } from '@/utils/formatters';
import processSignInEvent from './utils/analytics/processSignInEvent';

Vue.config.productionTip = false;
Vue.use(VueExpandableImage);
Vue.use(Vuelidate);
Vue.use(VueMeta);
Vue.use(VueLetterAvatar);
Vue.use(ReactiveProvide);

/* eslint-disable no-underscore-dangle */
Vue.prototype.$confirmModal = Vue.observable({
  open(fn) {
    if (fn) {
      this.reset();
      fn(this);
    }

    this._props.visibility = true;
  },
  close() {
    this._props.visibility = false;
  },
  reset() {
    this.setTitle(undefined);
    this.setOkText(undefined);
    this.setCancelText(undefined);
    this.setSubmit(() => {});
  },
  setTitle(title) {
    this._props.text = title;
  },
  setOkText(okText) {
    this._props.okText = okText;
  },
  setCancelText(cancelText) {
    this._props.cancelText = cancelText;
  },
  setSubmit(submitFn) {
    this._props.submit = submitFn;
  },
  setCancelHandler(cancelFn) {
    this._handlers.cancel = cancelFn;
  },
  _props: {
    text: undefined,
    okText: undefined,
    cancelText: undefined,
    visibility: false,
    submit: () => {},
  },
  _handlers: {
    cancel: () => { },
    close: (value) => {
      Vue.prototype.$confirmModal._props.visibility = value;
      Vue.prototype.$confirmModal.reset();
    },
  },
});
/* eslint-enable no-underscore-dangle */

Vue.directive('focus-on-create', {
  bind(el) {
    Vue.nextTick(() => el.focus());
  },
});

Vue.directive('initial-focus', {
  bind(el) {
    // after all focus on create triggered
    setTimeout(() => {
      el.focus();
    }, 50);
  },
});

Vue.directive('block-Y-scroll', {
  bind(el) {
    el.addEventListener('touchstart', (e) => {
      e.preventDefault();
    });
  },
});

function summaryClickListener() {
  const body = this.nextElementSibling;
  if (body.style.display && body.style.display !== 'none') {
    this.setAttribute('aria-expanded', 'false');
    body.style.display = 'none';
  } else {
    this.setAttribute('aria-expanded', 'true');
    body.style.display = 'block';
  }
}

Vue.directive('details', {
  bind(el) {
    const summary = el.children[0];
    summary.addEventListener('click', summaryClickListener);
  },
});

Vue.filter('titlecase', value => value.charAt(0).toUpperCase() + value.slice(1));

Vue.filter('formatNumber', formatNumber);

// Resize Twiframe height after message from iframe arrived
window.addEventListener('message', (e) => {
  if (e.origin !== 'https://twitframe.com') return;

  if (e.data.height && e.data.element.match(/^tweet_/)) {
    // console.log(`Setting height for ${e.data.element}`, e.data.height);
    document.getElementById(e.data.element).setAttribute('height', e.data.height);
  }
});

let app;

function renderIfNoApp(props = {}) {
  const { container } = props;

  if (!app) {
    app = new Vue({
      router,
      store,
      render: h => h(App),
    }).$mount(container ? container.querySelector('#app') : '#app');
  }
}

// as 2 onAuthStateChanged listeners are used, this flag
// is prevent their parallel running
let isAuthStateProcessing = false;

onAuthStateChanged(auth, async (user) => {
  if (isAuthStateProcessing) {
    return;
  }

  isAuthStateProcessing = true;

  const isAuthenticated = store.getters['auth/isAuthenticated'];

  if (!isAuthenticated) {
    await store.dispatch('auth/onAuthStateChanged', user);
  }

  renderIfNoApp();

  // TODO: need to move inside nuxt app
  await processSignInEvent(user);
  isAuthStateProcessing = false;
});

function renderWhenAuthed(props) {
  const unsubscribeObserver = onAuthStateChanged(auth, async (user) => {
    if (isAuthStateProcessing) {
      return;
    }

    isAuthStateProcessing = true;

    // FIXME: currently it sends 2 requests from the local and global listener
    // TODO: prevent to set if already set
    const isAuthenticated = store.getters['auth/isAuthenticated'];

    if (!isAuthenticated) {
      await store.dispatch('auth/onAuthStateChanged', user);
    }

    renderIfNoApp(props);

    // delete it as we have a global one
    unsubscribeObserver();
    isAuthStateProcessing = false;
  });
}

// eslint-disable-next-line no-underscore-dangle
if (!window.__POWERED_BY_QIANKUN__) {
  renderWhenAuthed();
}

export async function bootstrap() {
  // a required lifecycle function to export
}

export async function mount(props) {
  const inheritedCurrentUser = props.auth.getFirebaseUser();

  if (inheritedCurrentUser) {
    // this action will trigger onAuthStateChanged if users not equal
    await auth.updateCurrentUser(inheritedCurrentUser);
  }

  // this is needed when a user navigate through nuxt -> spa -> nuxt -> spa
  renderWhenAuthed(props);
}

export async function unmount() {
  app.$destroy();
  app.$el.innerHTML = '';
  app = null;
}
